/* AI Company Assessment + cost savings math. */

export const CALL_URL = '/borg-for-business/#contact'
export const ASSESSMENT_STORAGE_KEY = 'aas-assess-v5'
export const EXIT_STORAGE_KEY = 'aas-exit'

export const SEAT = 30 * 12
export const INSTALL = 5000

export const TEAM_SIZE_OPTIONS = ['10–25', '26–50', '51–100', '101–250', '250+'] as const
export const INDUSTRY_OPTIONS = [
  'Software / SaaS',
  'Marketing & agency',
  'Professional services',
  'Financial services',
  'Healthcare',
  'E-commerce & retail',
  'Manufacturing',
  'Construction & home services',
  'Other',
] as const
export const REVENUE_OPTIONS = ['Under $5M', '$5M–$10M', '$10M–$30M', '$30M+'] as const

export type TeamSize = (typeof TEAM_SIZE_OPTIONS)[number]

export const TEAM_MIDPOINT: Record<TeamSize, number> = {
  '10–25': 18,
  '26–50': 38,
  '51–100': 75,
  '101–250': 175,
  '250+': 300,
}

export type ToolItem = [name: string, slug: string, mono?: string]
export type ToolGroup = { g: string; items: ToolItem[] }

export const TOOLS: ToolGroup[] = [
  { g: 'AI assistants & models', items: [
    ['Claude', 'claude'], ['ChatGPT', '', 'GPT'], ['Gemini', 'googlegemini'], ['Copilot', '', 'Co'],
    ['Perplexity', 'perplexity'], ['Grok', '', 'G'], ['DeepSeek', 'deepseek'], ['Kimi', 'kimi'],
    ['Mistral', 'mistralai'], ['Cursor', 'cursor'],
  ]},
  { g: 'Communication', items: [
    ['Slack', '', 'S'], ['Microsoft Teams', '', 'T'], ['Google Workspace', 'google'], ['Zoom', 'zoom'], ['Loom', 'loom'],
  ]},
  { g: 'Projects & docs', items: [
    ['ClickUp', 'clickup'], ['Asana', 'asana'], ['Monday.com', '', 'm'], ['Trello', 'trello'],
    ['Jira', 'jira'], ['Linear', 'linear'], ['Notion', 'notion'], ['Airtable', 'airtable'],
  ]},
  { g: 'CRM & sales', items: [
    ['HubSpot', 'hubspot'], ['Salesforce', '', 'SF'], ['GoHighLevel', '', 'HL'], ['Pipedrive', '', 'P'],
    ['Zoho', 'zoho'], ['Intercom', 'intercom'],
  ]},
  { g: 'Automation & dev', items: [
    ['Zapier', 'zapier'], ['Make', 'make'], ['n8n', 'n8n'], ['GitHub', 'github'], ['Vercel', 'vercel'], ['Supabase', 'supabase'],
  ]},
  { g: 'Finance & ops', items: [
    ['QuickBooks', 'quickbooks'], ['Xero', 'xero'], ['Stripe', 'stripe'], ['Gusto', 'gusto'],
  ]},
  { g: 'Marketing & web', items: [
    ['Mailchimp', 'mailchimp'], ['Semrush', 'semrush'], ['Figma', 'figma'], ['Canva', '', 'C'],
    ['Webflow', 'webflow'], ['WordPress', 'wordpress'], ['Shopify', 'shopify'],
  ]},
]

export const AI_GROUP = TOOLS[0]!.items.map((t) => t[0])

export type DimKey = 'visibility' | 'productivity' | 'cost' | 'techstack' | 'bloat' | 'confidence'

export type DimMeta = {
  label: string
  lead: string
  install: string
  low: string
  mid: string
  high: string
}

export const DIMS: Record<DimKey, DimMeta> = {
  visibility: {
    label: 'Visibility',
    lead: 'Can you see the work, or only the seats?',
    install: 'Leader View on Borg: every person, every task, every model, live.',
    low: "You can't see the work. Every seat is a private experiment and you're the last to know.",
    mid: "You see usage, not the work. Seat counts aren't sightlines.",
    high: 'You can see the work. The install makes it per seat, per day, per metric.',
  },
  productivity: {
    label: 'Productivity',
    lead: 'Is the AI work moving a number you run the business on?',
    install: 'KPI mapping: every task tagged to a metric as it happens, unmapped work flagged.',
    low: "AI activity isn't tied to a number you run the business on, so nobody can prove it's working.",
    mid: "Some work maps to a metric. Most doesn't, so the scorecard can't tell what moved it.",
    high: 'Work maps to metrics. Now make it visible per seat and score it.',
  },
  cost: {
    label: 'Cost',
    lead: 'Do you know what AI costs you, and is the spend routed?',
    install: 'Borg Fusion routing plus per-team budgets: everyday work on inexpensive models, spend visible before the invoice.',
    low: "You don't know what AI costs you, and your team is on whatever model is open in the tab.",
    mid: 'You know the big subscriptions. The token spend underneath is unmanaged.',
    high: 'Spend is known and routed. Budgets keep it that way as you grow.',
  },
  techstack: {
    label: 'Tech stack',
    lead: "What's installed, and who decided it was safe?",
    install: 'The vetted registry: every tool evaluated first, tier permissions on every install.',
    low: "Nobody knows what's installed or who can install it. Your registry is GitHub.",
    mid: "There's a list and someone reviews it, case by case. It doesn't scale with the team.",
    high: 'Installs are controlled. The registry makes the review consistent and logged.',
  },
  bloat: {
    label: 'Team bloat',
    lead: 'What happens to cost and oversight when you grow?',
    install: 'One governed layer, unlimited seats: growth stops costing you oversight.',
    low: 'Overlapping per-seat subscriptions, and the bill grows with every hire.',
    mid: "One plan per person. Oversight doesn't scale with the seats.",
    high: 'One layer, flat cost. Keep it that way.',
  },
  confidence: {
    label: 'Confidence in the future',
    lead: 'Will this setup still be here in a year, and are you ready for the next one?',
    install: 'Install, train, future-proof: a new model is a policy change, and we re-tune quarterly.',
    low: 'You expect to rip this out next quarter, and you feel exposed. That\'s the setup, not you.',
    mid: 'Mostly confident, with no plan for the next model drop.',
    high: 'Confident, with a plan. We keep the plan current.',
  },
}

export type ScoredQuestion = {
  d: DimKey
  q: string
  h: string
  o: [label: string, points: number][]
}

export const QUESTIONS: ScoredQuestion[] = [
  { d: 'visibility', q: 'Could you name what each person on your team used AI for yesterday?', h: "Not what they're allowed to do. What they did.", o: [
    ["Yes. It's in a view I open every morning.", 3], ['For my direct reports, if I asked.', 2], ['I could guess.', 1], ['No idea.', 0],
  ]},
  { d: 'visibility', q: 'Someone pastes a client contract into a public AI tool. How do you find out?', h: 'Your largest client\'s lawyer will ask this one.', o: [
    ["Same day. It's logged and flagged.", 3], ['IT might catch it eventually.', 1], ['Only if it becomes a problem.', 0], ["We told people not to. That's the plan.", 0],
  ]},
  { d: 'productivity', q: 'How much of your team\'s AI work is tied to a number you track?', h: 'Activity is easy to see. Results are what you pay for.', o: [
    ['All of it. Every task maps to a metric.', 3], ['Most of it.', 2], ['Some of it.', 1], ["None, or we don't have a north-star metric.", 0],
  ]},
  { d: 'cost', q: 'What did AI cost you last month, across every seat, tool, and API key?', h: 'Seats, add-ons, extensions, and the card someone expensed.', o: [
    ['I know it to the dollar, by team.', 3], ['Roughly.', 2], ['The big subscriptions only.', 1], ['No idea.', 0],
  ]},
  { d: 'cost', q: 'Is everyday work running on the cheapest model that can do it?', h: 'Sorting tickets on a frontier model costs about 40× what it should.', o: [
    ['Yes. Routed by task, automatically.', 3], ["There's a guideline. Nobody enforces it.", 1], ["Whatever's open in the tab.", 0], ["I didn't know that mattered.", 0],
  ]},
  { d: 'techstack', q: 'Who decides whether a new AI tool is safe to install?', h: '"Whoever wants it" is the most common answer we hear.', o: [
    ['A registry and a review.', 3], ['IT, case by case.', 2], ['Managers.', 1], ['Whoever wants it.', 0],
  ]},
  { d: 'bloat', q: 'You add 20 people next quarter. What happens to your AI cost and your oversight?', h: 'Growth should get cheaper per person, not more blind.', o: [
    ['Cost stays flat. Oversight scales with them.', 3], ["Cost scales. Oversight doesn't.", 1], ['Both get worse.', 0], ["We'd figure it out.", 0],
  ]},
  { d: 'confidence', q: 'Last one. How ready is your company for the next two years of AI?', h: 'Ready means never at risk: not from a new model, a rogue install, or a bill.', o: [
    ['Fully. We have a plan and a system.', 3], ['Mostly.', 2], ['Not really.', 1], ["We're exposed.", 0],
  ]},
]

export const STEPS = ['company', 'stack', ...QUESTIONS.map((_, i) => `q${i}`), 'contact'] as const
export const TOTAL_STEPS = STEPS.length

export type AssessmentState = {
  team: TeamSize | ''
  industry: string
  revenue: string
  tools: string[]
  otherTools: string
  usage: number
  ans: number[]
  name: string
  email: string
  phone: string
  company?: string
  role: string
}

export const DEFAULT_STATE = (): AssessmentState => ({
  team: '',
  industry: '',
  revenue: '',
  tools: [],
  otherTools: '',
  usage: 40,
  ans: Array(QUESTIONS.length).fill(-1),
  name: '',
  email: '',
  phone: '',
  role: '',
})

export function money(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`
}

export function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!))
}

/** Cost savings calculator: per-seat plans vs install + ongoing (1/8 of seats). */
export function calcSeatSavings(n: number) {
  const plans = n * SEAT
  const ongoing = plans / 8
  const y1 = INSTALL + ongoing
  return {
    n,
    plans,
    ongoing,
    y1,
    yearOneSavings: plans - y1,
    ongoingSavings: plans - ongoing,
    barY1: Math.max(4, (y1 / plans) * 100),
    barOn: Math.max(4, (ongoing / plans) * 100),
  }
}

export function scoreAssessment(A: AssessmentState) {
  const dims: Record<string, number> = {}
  for (const d of Object.keys(DIMS) as DimKey[]) {
    const qs = QUESTIONS.map((q, i) => (q.d === d ? i : -1)).filter((i) => i >= 0)
    const pts = qs.reduce((s, i) => {
      const pick = A.ans[i] ?? -1
      return s + (pick >= 0 ? QUESTIONS[i]!.o[pick]![1] : 0)
    }, 0)
    dims[d] = Math.round((pts / (qs.length * 3)) * 100)
  }
  const total = Math.round(Object.values(dims).reduce((a, b) => a + b, 0) / Object.keys(dims).length)
  return { dims, total }
}

export function verdictFor(total: number): [tone: string, title: string, body: string] {
  if (total >= 75) {
    return ['g', 'Prepared, and worth protecting', "You've done what most leaders haven't. The exposure now is drift: a new model, a new hire, a new tool. The install makes today's setup permanent and visible."]
  }
  if (total >= 50) {
    return ['a', 'Exposed in places', 'Parts of your AI setup are governed and parts are running on trust. The gaps below are exactly where a rogue install, a runaway bill, or an unmeasured quarter comes from.']
  }
  if (total >= 25) {
    return ['r', 'At risk', "Your team is using AI faster than you can see it. The work isn't tied to your numbers, the spend isn't governed, and the stack is growing without a gate. This is the situation we install for."]
  }
  return ['r', 'Flying blind', "You have no film. Fifty private experiments, no oversight, no structure, and a bill and a risk profile that grow with every hire. The good news is that this is the fastest situation to fix, because there's nothing to migrate."]
}

export function qIndex(name: string): number {
  return name && name[0] === 'q' && name.length <= 3 ? Number(name.slice(1)) : -1
}

export function loadAssessment(): { A: AssessmentState; step: number } {
  const blank = DEFAULT_STATE()
  if (typeof localStorage === 'undefined') return { A: blank, step: 0 }
  try {
    const raw = localStorage.getItem(ASSESSMENT_STORAGE_KEY)
    if (!raw) return { A: blank, step: 0 }
    const d = JSON.parse(raw) as { A?: Partial<AssessmentState>; step?: number }
    const A = { ...blank, ...d.A }
    if (!Array.isArray(A.ans) || A.ans.length !== QUESTIONS.length) A.ans = Array(QUESTIONS.length).fill(-1)
    if (!Array.isArray(A.tools)) A.tools = []
    return { A, step: d.step || 0 }
  } catch {
    return { A: blank, step: 0 }
  }
}

export function saveAssessment(A: AssessmentState, step: number): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(ASSESSMENT_STORAGE_KEY, JSON.stringify({ A, step: Math.min(step, TOTAL_STEPS - 1) }))
  } catch { /* ignore */ }
}

export function clearAssessment(): void {
  if (typeof localStorage === 'undefined') return
  try { localStorage.removeItem(ASSESSMENT_STORAGE_KEY) } catch { /* ignore */ }
}
